import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Quote } from "lucide-react";
import Reveal, { SectionLabel } from "../components/Reveal";
import {
  BRAND,
  FOCUS_AREAS,
  PROGRAMS,
  PARENT_PILLARS,
  TESTIMONIALS,
  HERO_IMAGE,
  BEYOND_PILLARS,
  GROWTH_MODEL,
} from "../lib/site";

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 noise" />

        {/* gold corner accents */}
        <div className="absolute top-28 left-6 lg:left-10 h-px w-24 bg-[#d4af37] shimmer-line" />
        <div className="absolute bottom-16 right-6 lg:right-10 h-px w-24 bg-[#d4af37] shimmer-line" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8 fade-up">
              <SectionLabel testId="hero-label">A Premium Enrichment Academy</SectionLabel>
              <h1
                data-testid="hero-headline"
                className="mt-8 font-serif text-[44px] sm:text-6xl lg:text-[84px] xl:text-[96px] font-medium leading-[0.98] tracking-[-0.01em] text-white uppercase"
              >
                Beyond The <span className="gold-text">Stage</span>
                <br />
                <span className="text-white/90">Academy</span>
              </h1>
              <p
                data-testid="hero-tagline"
                className="mt-8 font-italic-accent italic text-2xl lg:text-4xl text-[#d4af37] tracking-wide"
              >
                Building Voices. Shaping Futures.
              </p>
              <p className="mt-7 max-w-2xl font-sans text-base lg:text-lg text-white/65 leading-relaxed font-light">
                Helping children and teens develop confidence,
                communication, leadership, creativity and self-expression — far
                beyond the classroom.
              </p>

              <div className="mt-12 flex flex-wrap items-center gap-5">
                <Link
                  to="/admissions"
                  data-testid="hero-cta-apply"
                  className="btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 inline-flex items-center gap-3 font-semibold"
                >
                  Apply Now <ArrowUpRight size={16} />
                </Link>
                <Link
                  to="/programs"
                  data-testid="hero-cta-discovery"
                  className="font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 inline-flex items-center gap-3 border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all duration-500 font-semibold"
                >
                  Explore Programs
                </Link>
              </div>
            </div>

            <div className="hidden lg:flex lg:col-span-4 justify-end">
              <div className="relative">
                <div className="absolute -inset-8 rounded-full bg-[#d4af37]/10 blur-2xl" />
                <img
                  src={BRAND.logo}
                  alt="BTSA"
                  className="relative h-80 w-80 xl:h-96 xl:w-96 object-contain mix-blend-screen drop-shadow-[0_30px_60px_rgba(212,175,55,0.25)]"
                />
              </div>
            </div>
          </div>

          {/* stats */}
          <div className="mt-20 lg:mt-28">
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-2xl">
              {[
                ["6–16", "Age Range"],
                ["100%", "Personalised"],
              ].map(([n, l]) => (
                <div key={l} className="bg-[#0a0a0a] px-6 py-7">
                  <div className="font-serif text-3xl lg:text-4xl gold-text">{n}</div>
                  <div className="mt-2 section-label">{l}</div>
                </div>
              ))}
            </div>
            <p className="mt-5 font-sans text-xs lg:text-sm text-white/45 italic max-w-2xl">
              Batch sizes are kept intentionally small and depend on the number of admissions each term.
            </p>
          </div>
        </div>
      </section>

      {/* BEYOND MARKS — six pillars (ivory) */}
      <section
        data-testid="beyond-marks-section"
        className="ivory-section relative py-24 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>The BTSA Difference</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.04]">
                Beyond Marks.
                <br />
                Beyond Memorisation.
                <br />
                <span className="gold-text italic">Beyond Limits.</span>
              </h2>
              <p className="mt-7 font-sans text-base lg:text-lg leading-relaxed max-w-2xl">
                We do not build performers. We build confident individuals and
                future leaders — nurtured across six lifelong capabilities that
                school marks alone can never measure.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/5 border border-black/5">
            {BEYOND_PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div
                  data-testid={`beyond-pillar-${i}`}
                  className="lux-card border-0 p-8 h-full group cursor-default"
                >
                  <div className="font-serif text-3xl gold-text">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-light">{p.title}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed">{p.desc}</p>
                  <div className="mt-6 h-px w-0 group-hover:w-16 transition-all duration-700 bg-[#b38728]" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GROWTH MODEL */}
      <section
        data-testid="growth-model-section"
        className="relative py-24 lg:py-32 bg-[#080808] border-y border-white/5 overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 gold-divider" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal>
              <div className="flex justify-center">
                <SectionLabel>The BTSA Growth Model</SectionLabel>
              </div>
              <h2 className="mt-7 font-serif text-4xl lg:text-5xl font-light text-white leading-[1.05]">
                From first words to
                <br />
                <span className="gold-text italic">confident leadership.</span>
              </h2>
            </Reveal>
          </div>

          <div className="mt-20 relative">
            {/* connecting line */}
            <div className="hidden lg:block absolute top-7 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/40 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-y-12 gap-x-6">
              {GROWTH_MODEL.map((g, i) => (
                <Reveal key={g.step} delay={i * 0.08}>
                  <div data-testid={`growth-step-${i}`} className="relative text-center">
                    <div className="relative mx-auto h-14 w-14 grid place-items-center rounded-full border border-[#d4af37]/50 bg-[#0a0a0a]">
                      <span className="font-serif text-lg gold-text">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-6 font-serif text-xl text-white font-light uppercase tracking-wide">
                      {g.step}
                    </h3>
                    <p className="mt-3 font-sans text-sm text-white/55 leading-relaxed max-w-[15rem] mx-auto">
                      {g.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY BTSA */}
      <section
        data-testid="why-section"
        className="relative py-24 lg:py-32 bg-[#0a0a0a]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>Why Beyond The Stage</SectionLabel>
                <h2 className="mt-6 font-serif text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.05]">
                  Seven disciplines.
                  <br />
                  <span className="gold-text italic">One confident child.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.15}>
                <p className="font-sans text-base lg:text-lg text-white/60 leading-relaxed">
                  We do not teach subjects. We cultivate capabilities — the
                  voice, the poise, the imagination and the character that a
                  child carries through every stage of life.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {FOCUS_AREAS.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.05}>
                <div
                  data-testid={`focus-${i}`}
                  className="bg-[#0a0a0a] hover:bg-[#111] transition-colors duration-500 p-8 h-full group cursor-default"
                >
                  <div className="font-serif text-3xl gold-text">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-white font-light">
                    {f.title}
                  </h3>
                  <p className="mt-3 font-sans text-sm text-white/55 leading-relaxed">
                    {f.desc}
                  </p>
                  <div className="mt-6 h-px w-0 group-hover:w-16 transition-all duration-700 bg-[#d4af37]" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNATURE PROGRAMS */}
      <section
        data-testid="programs-section"
        className="relative py-24 lg:py-32 bg-[#080808] overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 gold-divider" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <Reveal>
              <SectionLabel>Signature Programs</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl lg:text-5xl font-medium uppercase tracking-wide text-white max-w-2xl leading-[1.05]">
                Curated journeys for
                <br />
                <span className="gold-text">every stage of growth.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/programs"
                data-testid="programs-view-all"
                className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#d4af37] inline-flex items-center gap-2 hover:gap-4 transition-all"
              >
                View All Programs <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <Link
                  to="/programs"
                  data-testid={`program-card-${p.slug}`}
                  className="lux-card block h-full group"
                >
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-[1.2s]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute top-5 left-5 section-label bg-black/60 backdrop-blur px-3 py-1">
                      {p.ageGroup}
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl text-white font-light">
                      {p.title}
                    </h3>
                    <p className="mt-3 font-sans text-sm text-white/55 leading-relaxed line-clamp-3">
                      {p.overview}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-[#d4af37] font-sans text-[11px] tracking-[0.28em] uppercase">
                      Explore <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PARENTS CHOOSE BTSA */}
      <section
        data-testid="parents-section"
        className="ivory-section relative py-24 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Why Parents Choose BTSA</SectionLabel>
              <h2 className="mt-6 font-serif text-4xl lg:text-5xl font-light text-white leading-[1.05]">
                A standard of care, beyond
                <span className="gold-text italic"> the ordinary.</span>
              </h2>
              <p className="mt-7 font-sans text-base text-white/60 leading-relaxed max-w-md">
                Discerning families choose BTSA for what we refuse to compromise
                on — intimacy, attention, craft and outcomes that last for
                life.
              </p>
              <Link
                to="/about"
                data-testid="parents-learn-more"
                className="mt-9 inline-flex items-center gap-3 font-sans text-[11px] tracking-[0.3em] uppercase text-[#d4af37]"
              >
                Our Philosophy <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
              {PARENT_PILLARS.map((pil, i) => (
                <Reveal key={pil.title} delay={i * 0.06}>
                  <div
                    data-testid={`pillar-${i}`}
                    className="bg-[#0a0a0a] p-8 h-full"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles size={16} className="text-[#d4af37]" />
                      <span className="section-label">0{i + 1}</span>
                    </div>
                    <h3 className="mt-5 font-serif text-xl text-white font-light">
                      {pil.title}
                    </h3>
                    <p className="mt-3 font-sans text-sm text-white/55 leading-relaxed">
                      {pil.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="relative py-24 lg:py-32 bg-[#080808] border-y border-white/5 overflow-hidden"
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#d4af37]/[0.05] blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <Reveal>
            <div className="text-center">
              <SectionLabel>Voices Of Our Families</SectionLabel>
            </div>
            <h2 className="mt-6 font-serif text-4xl lg:text-5xl font-light text-white text-center leading-[1.05]">
              The words of parents who
              <br />
              <span className="gold-text italic">trusted us with their children.</span>
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure
                  data-testid={`testimonial-${i}`}
                  className="lux-card p-9 h-full flex flex-col"
                >
                  <Quote size={28} className="text-[#d4af37]" />
                  <blockquote className="mt-6 font-serif text-xl lg:text-2xl text-white/90 font-light leading-snug">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-auto pt-8 flex items-center gap-4">
                    <div className="h-10 w-10 grid place-items-center hairline font-serif text-[#d4af37]">
                      {t.name.split(" ").map((p) => p[0]).join("")}
                    </div>
                    <div>
                      <div className="font-sans text-sm text-white">{t.name}</div>
                      <div className="font-sans text-xs text-white/45">{t.role}</div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="home-cta"
        className="relative py-28 lg:py-36 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#141414] to-[#0a0a0a]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-[0.10] blur-3xl gold-bg" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <SectionLabel testId="cta-label">
              <span className="block text-center w-full">Begin The Journey</span>
            </SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-7 font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.05]">
              Join our community of
              <br />
              <span className="gold-text italic">confident learners.</span>
            </h2>
            <p className="mt-7 font-sans text-base lg:text-lg text-white/60 max-w-xl mx-auto">
              A complimentary discovery session is the first step. We'll listen,
              observe and craft a journey designed for your child alone.
            </p>
            <div className="mt-12 flex flex-wrap justify-center gap-5">
              <Link
                to="/admissions"
                data-testid="cta-apply"
                className="btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5"
              >
                Apply Now
              </Link>
              <Link
                to="/contact"
                data-testid="cta-contact"
                className="font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all duration-500"
              >
                Speak With Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
